import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7271/api';

// Enums
export enum ReportPeriodType {
  Month = 1,
  Quarter = 2,
  Year = 3,
  Custom = 4
}

export enum PaymentStatusType {
  PaidInFull = 1,
  InInstallments = 2,
  HasDebt = 3
}

// Interfaces - Cash Flow Report
export interface MonthlyExpectedIncomeDto {
  monthOffset: number;
  monthName: string;
  expectedAmount: number;
}

export interface CustomerCashFlowDto {
  clientId: number;
  customerName: string;
  monthlyBenefitAmount: number;
  commissionPercentage: number;
  totalToCollect: number;
  collectionPeriodMonths: number;
  expectedMonthlyIncome: number;
  sixMonthsDistribution: MonthlyExpectedIncomeDto[];
}

export interface CashFlowReportDto {
  monthlyActualIncome: number;
  nextMonthExpectedIncome: number;
  threeMonthsExpectedIncome: number;
  customerDetails: CustomerCashFlowDto[];
}

// Interfaces - Collection and Debt Report
export interface PaymentStatisticsDto {
  customersPaidInFull: number;
  customersInInstallments: number;
  customersWithDebt: number;
  totalOpenDebts: number;
}

export interface CustomerDebtDto {
  clientId: number;
  customerName: string;
  totalCharge: number;
  totalPaid: number;
  balance: number;
  lastPaymentDate?: string;
  paymentStatus: PaymentStatusType;
}

export interface CollectionDebtReportDto {
  paymentStatistics: PaymentStatisticsDto;
  customerDetails: CustomerDebtDto[];
}

// Interfaces - Team Performance Report
export interface MonthlySummaryDto {
  totalNewLeads: number;
  totalSignatures: number;
  conversionPercentage: number;
  totalApprovedCases: number;
  totalBenefitAmount: number;
}

export interface SalesRepPerformanceDto {
  userId: number;
  name: string;
  leadsReceivedThisMonth: number;
  signedThisMonth: number;
  successPercentage: number;
}

export interface CaseManagerPerformanceDto {
  userId: number;
  name: string;
  casesManaged: number;
  approvedCases: number;
  totalBenefitAmount: number;
  expectedIncomeFromCases: number;
}

export interface TeamPerformanceReportDto {
  monthlySummary: MonthlySummaryDto;
  salesRepsPerformance: SalesRepPerformanceDto[];
  caseManagersPerformance: CaseManagerPerformanceDto[];
}

// Interfaces - Special Payments Report
export interface SpecialPaymentsReportDto {
  totalPending: number;
  totalPendingAmount: number;
  paidInPeriod: number;
  paidInPeriodAmount: number;
  cancelledInPeriod: number;
  payments: SpecialPaymentReportItemDto[];
}

export interface SpecialPaymentReportItemDto {
  id: number;
  clientId: number;
  clientName: string;
  amount: number;
  description: string;
  status: number;
  statusName: string;
  paymentMethod?: number;
  paymentMethodName?: string;
  paymentDate?: string;
  createdAt: string;
  createdByName: string;
}

// Report Filter
export interface ReportFilterDto {
  dateFrom?: string;
  dateTo?: string;
  periodType: ReportPeriodType;
}

// API Service
class ReportsApiService {
  private getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    return {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    };
  }

  async getCashFlowReport(filter: ReportFilterDto): Promise<CashFlowReportDto> {
    const response = await axios.post(
      `${API_BASE_URL}/reports/cash-flow`,
      filter,
      this.getAuthHeaders()
    );
    return response.data;
  }

  async getCollectionDebtReport(filter: ReportFilterDto): Promise<CollectionDebtReportDto> {
    const response = await axios.post(
      `${API_BASE_URL}/reports/collection-debt`,
      filter,
      this.getAuthHeaders()
    );
    return response.data;
  }

  async getTeamPerformanceReport(filter: ReportFilterDto): Promise<TeamPerformanceReportDto> {
    const response = await axios.post(
      `${API_BASE_URL}/reports/team-performance`,
      filter,
      this.getAuthHeaders()
    );
    return response.data;
  }

  async getSpecialPaymentsReport(filter: ReportFilterDto): Promise<SpecialPaymentsReportDto> {
    const response = await axios.post(
      `${API_BASE_URL}/reports/special-payments`,
      filter,
      this.getAuthHeaders()
    );
    return response.data;
  }
}

export const reportsApi = new ReportsApiService();

// Helper Functions
export const getReportPeriodName = (periodType: ReportPeriodType): string => {
  switch (periodType) {
    case ReportPeriodType.Month:
      return 'חודש';
    case ReportPeriodType.Quarter:
      return 'רבעון';
    case ReportPeriodType.Year:
      return 'שנה';
    case ReportPeriodType.Custom:
      return 'מותאם אישית';
    default:
      return 'חודש';
  }
};

export const getPaymentStatusName = (status: PaymentStatusType): string => {
  switch (status) {
    case PaymentStatusType.PaidInFull:
      return 'שולם במלואו';
    case PaymentStatusType.InInstallments:
      return 'תשלומים';
    case PaymentStatusType.HasDebt:
      return 'יש חוב';
    default:
      return 'לא ידוע';
  }
};

export const getPaymentStatusColor = (status: PaymentStatusType): string => {
  switch (status) {
    case PaymentStatusType.PaidInFull:
      return 'var(--success-color)';
    case PaymentStatusType.InInstallments:
      return 'var(--warning-color)';
    case PaymentStatusType.HasDebt:
      return 'var(--error-color)';
    default:
      return 'var(--text-secondary)';
  }
};

export const formatCurrency = (amount: number | undefined | null): string => {
  if (amount === undefined || amount === null) return '-';
  return `₪${amount.toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const formatDate = (dateString?: string): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('he-IL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

export const formatPercentage = (value: number | undefined | null): string => {
  if (value === undefined || value === null) return '-';
  return `${value.toFixed(2)}%`;
};

export const handleApiError = (error: any): string => {
  if (error.response) {
    return error.response.data || 'שגיאה בביצוע הפעולה';
  } else if (error.request) {
    return 'שגיאה בחיבור לשרת';
  } else {
    return 'שגיאה לא צפויה';
  }
};
